import type { FC } from "react";

import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { PlainTooltip } from "@similarweb/ui-components";

import { NOT_AVAILABLE } from "app/filters/const";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { Bullet } from "pro-features/styled components/StyledLabel/Bullet";

import { CubeWrapper } from "../../../key-indicators/single/key-indicators/components/Cube";

const { carbon } = colorsPalettes;

const LoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    width: 100%;
`;

const TitleContainer = styled.div`
    display: flex;
    align-content: center;
    gap: 4px;
    justify-content: space-between;
`;

const AverageContainer = styled.div`
    display: flex;
    align-content: center;
    gap: 4px;
    justify-content: space-between;
    align-items: center;
`;

const SubtitleContainer = styled.div`
    margin-top: 4px;
    ${setFontVariant("listItem3", carbon[300])};
`;

const Title = styled.span`
    ${setFontVariant("subTitle3Alt", carbon[500])};
`;

const AverageStatusText = styled.span<{ color: string }>`
    ${({ color }) => setFontVariant("subTitle3Alt", color)};
`;

const StyledIcon = styled(SWReactIcons)`
    svg {
        width: 20px;
        height: 20px;
        path {
            fill: ${carbon[200]};
        }
    }
`;

const ValContainer = styled.span`
    ${setFontVariant("h4", carbon[500])};
`;

interface ICube {
    title: string;
    tooltip: string;
    iconName?: IconName;
    data: string;
    isLoading?: boolean;
    subtitle?: string;
    averageStatus?: string;
    averageStatusColor?: string;
    onClick?: VoidFunction;
}

const Cube: FC<ICube> = ({
    title,
    tooltip,
    iconName,
    data,
    isLoading,
    subtitle,
    averageStatus,
    averageStatusColor,
    onClick,
}) => {
    const isClickable = data !== NOT_AVAILABLE && !!onClick;
    return (
        <>
            {isLoading ? (
                <LoaderContainer>
                    <LoadingSpinner />
                </LoaderContainer>
            ) : (
                <PlainTooltip tooltipContent={tooltip}>
                    <CubeWrapper
                        data-automation="Cube"
                        isClickable={isClickable}
                        onClick={isClickable ? onClick : null}
                    >
                        <div>
                            <TitleContainer>
                                <Title>{title}</Title>
                                {iconName ? <StyledIcon iconName={iconName} /> : null}
                            </TitleContainer>
                            <SubtitleContainer>{subtitle}</SubtitleContainer>
                        </div>
                        <div>
                            <ValContainer>{data}</ValContainer>
                            <AverageContainer>
                                <AverageStatusText color={averageStatusColor}>{averageStatus}</AverageStatusText>
                                <Bullet color={averageStatusColor} />
                            </AverageContainer>
                        </div>
                    </CubeWrapper>
                </PlainTooltip>
            )}
        </>
    );
};

export default Cube;
